/**
 * Pure TEA update for the sidecar entity lists shared across outline tabs
 * (Scratchpad notes, Database entries, Unplaced items).
 *
 * Shell: `useOutlineEntitiesStore.ts` (Pinia + invoke Cmds + root watcher).
 */
import { assertNever } from "../lib/assertNever";
import { isNothing, type Maybe } from "../lib/maybe";
import type {
  DatabaseEntrySummary,
  NoteEntry,
  UnplacedItem,
} from "../lib/outlineEntityTypes";
import type { UpdateFn } from "../lib/tea";

export type OutlineEntitiesModel = {
  readonly notes: NoteEntry[];
  readonly databaseEntries: string[];
  readonly databaseEntryFields: DatabaseEntrySummary[];
  readonly unplaced: UnplacedItem[];
};

export const initialOutlineEntitiesModel: OutlineEntitiesModel = {
  notes: [],
  databaseEntries: [],
  databaseEntryFields: [],
  unplaced: [],
};

/** Which list a failed load belongs to (for the log line). */
export type OutlineEntitiesList = "notes" | "database" | "unplaced";

export type OutlineEntitiesMsg =
  /** Refresh request. `root` is the session root at call time. */
  | { readonly type: "RefreshNotes"; readonly root: Maybe<string> }
  | { readonly type: "RefreshDatabase"; readonly root: Maybe<string> }
  | { readonly type: "RefreshUnplaced"; readonly root: Maybe<string> }
  /** Load results (also used as plain setters by the shell's writable refs). */
  | { readonly type: "NotesLoaded"; readonly notes: NoteEntry[] }
  | {
      readonly type: "DatabaseLoaded";
      readonly summaries: DatabaseEntrySummary[];
    }
  | { readonly type: "UnplacedLoaded"; readonly items: UnplacedItem[] }
  /** Plain setter for the name list alone (kept for the store's writable ref). */
  | { readonly type: "DatabaseEntryNamesSet"; readonly names: string[] }
  | {
      readonly type: "LoadFailed";
      readonly list: OutlineEntitiesList;
      readonly error: unknown;
    };

export type OutlineEntitiesCmd =
  | { readonly kind: "loadNotes"; readonly root: string }
  | { readonly kind: "loadDatabase"; readonly root: string }
  | { readonly kind: "loadUnplaced"; readonly root: string }
  | {
      readonly kind: "logError";
      readonly message: string;
      readonly error: unknown;
    };

export function loadFailureMessage(list: OutlineEntitiesList): string {
  switch (list) {
    case "notes":
      return "Failed to list notes";
    case "database":
      return "Failed to list database";
    case "unplaced":
      return "Failed to list unplaced items";
    default:
      return assertNever(list);
  }
}

export function updateOutlineEntities(
  model: OutlineEntitiesModel,
  msg: OutlineEntitiesMsg,
): readonly [OutlineEntitiesModel, readonly OutlineEntitiesCmd[]] {
  switch (msg.type) {
    case "RefreshNotes":
      if (isNothing(msg.root)) return [{ ...model, notes: [] }, []];
      return [model, [{ kind: "loadNotes", root: msg.root.value }]];
    case "RefreshDatabase":
      if (isNothing(msg.root)) {
        return [{ ...model, databaseEntries: [], databaseEntryFields: [] }, []];
      }
      return [model, [{ kind: "loadDatabase", root: msg.root.value }]];
    case "RefreshUnplaced":
      if (isNothing(msg.root)) return [{ ...model, unplaced: [] }, []];
      return [model, [{ kind: "loadUnplaced", root: msg.root.value }]];
    case "NotesLoaded":
      return [{ ...model, notes: msg.notes }, []];
    case "DatabaseLoaded":
      return [
        {
          ...model,
          databaseEntries: msg.summaries.map((s) => s.name),
          databaseEntryFields: msg.summaries,
        },
        [],
      ];
    case "DatabaseEntryNamesSet":
      return [{ ...model, databaseEntries: msg.names }, []];
    case "UnplacedLoaded":
      return [{ ...model, unplaced: msg.items }, []];
    case "LoadFailed":
      // Keep the previous list on failure (same as the pre-TEA store).
      return [
        model,
        [
          {
            kind: "logError",
            message: loadFailureMessage(msg.list),
            error: msg.error,
          },
        ],
      ];
    default:
      return assertNever(msg);
  }
}

export const updateOutlineEntitiesFn: UpdateFn<
  OutlineEntitiesModel,
  OutlineEntitiesMsg,
  OutlineEntitiesCmd
> = updateOutlineEntities;
